/**
 * Filters of the inbound / outbound lists. Pure helpers: the page keeps the state in the URL (shareable, survives
 * back/forward), converts it to the API query, and "Reset" is simply the empty state. No filter = every record the
 * seller may see (the API applies no hidden day/month window).
 */
export type ListSortValue = 'created_desc' | 'created_asc';

export type ListFilterState = {
  readonly status: string;
  readonly ownerId: string;
  readonly warehouseId: string;
  /** 'YYYY-MM-DD' (the user's local calendar day), or ''. */
  readonly from: string;
  readonly to: string;
  readonly sort: ListSortValue;
};

export const EMPTY_FILTERS: ListFilterState = { status: '', ownerId: '', warehouseId: '', from: '', to: '', sort: 'created_desc' };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function readFilters(params: URLSearchParams): ListFilterState {
  const day = (k: string): string => {
    const v = params.get(k) ?? '';
    return DAY.test(v) ? v : '';
  };
  return {
    status: params.get('status') ?? '',
    ownerId: params.get('client') ?? '',
    warehouseId: params.get('warehouse') ?? '',
    from: day('from'),
    to: day('to'),
    sort: params.get('sort') === 'created_asc' ? 'created_asc' : 'created_desc',
  };
}

/** Only non-default values reach the URL, so the empty state is a clean address. */
export function writeFilters(s: ListFilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (s.status) p.set('status', s.status);
  if (s.ownerId) p.set('client', s.ownerId);
  if (s.warehouseId) p.set('warehouse', s.warehouseId);
  if (s.from) p.set('from', s.from);
  if (s.to) p.set('to', s.to);
  if (s.sort !== 'created_desc') p.set('sort', s.sort);
  return p;
}

export function isFiltered(s: ListFilterState): boolean {
  return s.status !== '' || s.ownerId !== '' || s.warehouseId !== '' || s.from !== '' || s.to !== '' || s.sort !== 'created_desc';
}

/** The chosen last day is INCLUDED: the API's upper bound is exclusive, so it is the start of the next local day. */
export function rangeInvalid(s: ListFilterState): boolean {
  return s.from !== '' && s.to !== '' && s.from > s.to;
}

function localDayStart(day: string, plusDays: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d + plusDays, 0, 0, 0, 0).toISOString();
}

export type ListApiQuery = {
  status?: string;
  ownerId?: string;
  warehouseId?: string;
  createdFrom?: string;
  createdTo?: string;
  sort?: ListSortValue;
};

export function toApiQuery(s: ListFilterState): ListApiQuery {
  const q: ListApiQuery = {};
  if (s.status) q.status = s.status;
  if (s.ownerId) q.ownerId = s.ownerId;
  if (s.warehouseId) q.warehouseId = s.warehouseId;
  if (s.from) q.createdFrom = localDayStart(s.from, 0);
  // an inverted range is never sent: the bar shows a hint instead and the list keeps the lower bound only
  if (s.to && !rangeInvalid(s)) q.createdTo = localDayStart(s.to, 1);
  if (s.sort !== 'created_desc') q.sort = s.sort;
  return q;
}
